/**
 * The arithmetic behind revealing a draft (#804), kept apart from the component so the parts that
 * can be wrong on their own can be asked on their own.
 */

/**
 * How long the whole reveal takes, whatever the draft says.
 *
 * The acceptance range is one to two seconds; this sits in the middle so neither a two-line
 * confirmation nor a full quotation can drift out of it. Duration is fixed rather than a
 * per-character delay for exactly that reason: a per-character delay makes a long draft take
 * proportionally longer, and a long draft is the common case.
 */
export const REVEAL_DURATION_MS = 1400;

type GraphemeSegmenter = new (
  locales?: string | string[],
  options?: { granularity?: "grapheme" | "word" | "sentence" },
) => { segment(input: string): Iterable<{ segment: string }> };

/**
 * Split text the way a reader sees it, not the way it is stored.
 *
 * `"ä"` can be one code point or two, an emoji can be five, and `.split("")` cuts through all of
 * them — which renders a broken glyph mid-animation and, worse, is invisible in tests written with
 * ASCII. `Intl.Segmenter` is the only thing that gets this right; where it is missing,
 * `Array.from` at least respects surrogate pairs instead of halving them.
 */
export function graphemes(text: string): string[] {
  // Described structurally rather than as `Intl.Segmenter`: the type ships in a newer lib than
  // this project targets, and only these two members are used.
  const Segmenter = (Intl as unknown as { Segmenter?: GraphemeSegmenter }).Segmenter;
  if (typeof Segmenter === "function") {
    const segments = new Segmenter(undefined, { granularity: "grapheme" }).segment(text);
    return Array.from(segments, (part) => part.segment);
  }
  return Array.from(text);
}

/**
 * How much of the draft is visible after `elapsedMs`.
 *
 * Driven by elapsed time rather than by counting frames, so a slow frame or a backgrounded tab
 * cannot stretch the reveal past its promise — it lands on the same total whatever the machine is
 * doing. Rounds up, so the very first frame already shows something and the animation reads as
 * having started.
 */
export function revealedCount(
  elapsedMs: number,
  total: number,
  durationMs: number = REVEAL_DURATION_MS,
): number {
  if (total <= 0) return 0;
  if (!(elapsedMs > 0)) return 0;
  if (durationMs <= 0 || elapsedMs >= durationMs) return total;
  return Math.min(total, Math.ceil((elapsedMs / durationMs) * total));
}

/** Whether this session has asked not to be animated at. Absent `matchMedia`, assume motion is
 *  fine — every browser the workspace supports has it, and a test environment is not a person. */
export function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}
